import {
  Button,
  Col,
  Drawer,
  Row,
  List,
  Space,
  Typography,
  Divider,
  Badge,
  Collapse,
  Tooltip,
  DatePicker,
} from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { paymentMethods } from "../../Enums/PaymentMethod.enums";
import { contractsStatus } from "../../Enums/ContractsStatus.enums";
import {
  CloseOutlined,
  FileTextOutlined,
  MailOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import ProfilePermissionService from "../../services/profilePermissions.service";
import { formatCurrency } from "../../services/utils";
import { frequencies } from "../../Enums/Frequency.enums";
import { contractsRepeat } from "../../Enums/ContractsRepeat.enums";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getById as getContractById } from "../../services/contracts.service";
import ConfirmModal from "../../components/Commons/Modal/confirm-modal.component";
import { sendInvoice, updateStatus } from "../../services/invoices.service";
import { AlertService } from "../../services/alert.service";
import dayjs from "dayjs";

const { Text } = Typography;

interface Details {
  details: any;
  openDetails: boolean;
  onCloseDetails: any;
}

export default function DetailContract({
  details,
  openDetails,
  onCloseDetails,
}: Details) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const profilePermissionService = ProfilePermissionService.getInstance();

  const statusColor = {
    Active: "#76c12b",
    Inactive: "#e59401",
    Canceled: "#d9421d",
  };
  const [sendingInvoiceLoading, setSendingInvoiceLoading] = useState<
    string | null
  >(null);
  const [openSendInvoiceConfirm, setOpenSendInvoiceConfirm] = useState<
    string | null
  >(null);

  const [payInvoiceLoading, setPayInvoiceLoading] = useState<string | null>(
    null
  );
  const [openPayInvoiceConfirm, setOpenPayInvoiceConfirm] = useState<
    string | null
  >(null);

  const [paymentDate, setPaymentDate] = useState(dayjs());

  const { mutate: payInvoiceMutate } = useMutation(updateStatus, {
    onSuccess: () => {
      AlertService.sendAlert([
        { text: "Fatura quitada com sucesso.", type: "success" },
      ]);
      setPayInvoiceLoading(null);
      setOpenPayInvoiceConfirm(null);
      queryClient.invalidateQueries(["contractDetails", details?.uuid]);
    },
    onError: () => {
      AlertService.sendAlert([
        { text: "Falha ao quitar fatura.", type: "error" },
      ]);
      setPayInvoiceLoading(null);
    },
  });

  const { mutate: sendInvoiceMutate } = useMutation(sendInvoice, {
    onSuccess: () => {
      AlertService.sendAlert([
        { text: "Fatura enviada por e-mail com sucesso.", type: "success" },
      ]);
      setSendingInvoiceLoading(null);
    },
    onError: () => {
      AlertService.sendAlert([
        { text: "Falha ao enviar fatura por e-mail.", type: "error" },
      ]);
      setSendingInvoiceLoading(null);
    },
  });
  const { data: contractDetailsData } = useQuery(
    ["contractDetails", details?.uuid],
    () => getContractById(details?.uuid),
    { enabled: !!details?.uuid, refetchOnWindowFocus: false }
  );
  const fullDetails = contractDetailsData?.data || details;
  const contractSales =
    fullDetails?.sales ||
    fullDetails?.contract_sales ||
    fullDetails?.contractSales ||
    [];
  const emittedCharges = contractSales
    .map((sale: any) =>
      (sale?.invoices || []).map((inv: any) => ({
        sale,
        invoice: inv,
      }))
    )
    .flat();

  return (
    <Drawer
      title={
        <Badge
          color={statusColor[details?.status as keyof typeof statusColor]}
          text={`Contrato #${details?.id} ${
            contractsStatus.find((_status) => _status.value === details?.status)
              ?.label || ""
          }
          `}
        />
      }
      placement="right"
      closeIcon={false}
      width={500}
      open={openDetails}
      onClose={onCloseDetails}
      extra={
        <Button
          type="text"
          shape="circle"
          size="middle"
          icon={<CloseOutlined />}
          onClick={onCloseDetails}
        />
      }
    >
      {details && (
        <>
          <Row>
            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Cliente</Text>
                <Text>{details.customer.name}</Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Plano</Text>
                <Text>{details.plan.name}</Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Data Início</Text>
                <Text>
                  {details.start_date
                    ? moment(details.start_date, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )
                    : ""}
                </Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Data Fim</Text>
                <Text>
                  {details.end_date
                    ? moment(details.end_date, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )
                    : "Indeterminado"}
                </Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Status</Text>
                <Text>
                  {contractsStatus.find(
                    (_status) => _status.value === details.status
                  )?.label || ""}
                </Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Vendedor</Text>
                <Text>{details.seller.name}</Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Data da Emissão</Text>
                <Text>
                  {details.billing_date
                    ? moment(details.billing_date, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )
                    : ""}
                </Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Frequência</Text>
                <Text>
                  {frequencies.find(
                    (frequency) => frequency.value === details.frequency
                  )?.label || ""}
                </Text>
              </Space>
            </Col>

            <Col span={24} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Repetição</Text>
                <Text>
                  {contractsRepeat.find(
                    (repeat) => repeat.value === details.repeat
                  )?.label || ""}
                </Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Valor Total</Text>
                <Text>{formatCurrency(details.total_amount || 0)}</Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Desconto</Text>
                <Text>{details.discount}%</Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Meio de Pagamento</Text>
                <Text>
                  {paymentMethods.find(
                    (method) => method.value === details.payment_method
                  )?.label || ""}
                </Text>
              </Space>
            </Col>

            <Col span={24} style={{ marginBottom: 15 }}>
              <Collapse defaultActiveKey={[]}>
                <Collapse.Panel
                  header={<Text strong>Serviços</Text>}
                  key="services"
                >
                  <List
                    itemLayout="horizontal"
                    dataSource={details.contractServices}
                    renderItem={(item: any, index) => (
                      <List.Item>
                        <List.Item.Meta
                          title={item?.service?.name || "Serviço"}
                          description={`Preço: ${formatCurrency(
                            item?.price || 0
                          )} | Quantidade: ${item?.quantity || 0}`}
                        />
                      </List.Item>
                    )}
                  />
                </Collapse.Panel>
              </Collapse>
            </Col>

            <Col span={24} style={{ marginBottom: 15 }}>
              <Collapse defaultActiveKey={[]}>
                <Collapse.Panel
                  header={<Text strong>Cobranças emitidas</Text>}
                  key="charges"
                >
                  <List
                    itemLayout="horizontal"
                    dataSource={emittedCharges}
                    renderItem={(item: any) => (
                      <List.Item
                        actions={[
                          <Tooltip
                            title="Abrir fatura"
                            key={`open_${item?.invoice?.uuid}`}
                          >
                            <Button
                              type="link"
                              size="small"
                              icon={<FileTextOutlined />}
                              onClick={() =>
                                window.open(
                                  `/invoices/${item?.invoice?.uuid}`,
                                  "_blank"
                                )
                              }
                            />
                          </Tooltip>,
                          <Tooltip
                            title="Quitar fatura"
                            key={`pay_${item?.invoice?.uuid}`}
                          >
                            <Button
                              style={{
                                display:
                                  item?.invoice?.status === "Paid"
                                    ? "none"
                                    : "block",
                              }}
                              type="link"
                              size="small"
                              loading={
                                payInvoiceLoading === item?.invoice?.uuid
                              }
                              icon={<CheckCircleOutlined />}
                              onClick={() =>
                                setOpenPayInvoiceConfirm(item?.invoice?.uuid)
                              }
                              disabled={
                                !["Pending", "Pendente"].includes(
                                  item?.invoice?.status || ""
                                )
                              }
                            />
                          </Tooltip>,
                          <Tooltip
                            title="Enviar fatura por e-mail"
                            key={`send_${item?.invoice?.uuid}`}
                          >
                            <Button
                              style={{
                                display:
                                  item?.invoice?.status === "Paid"
                                    ? "none"
                                    : "block",
                              }}
                              type="link"
                              size="small"
                              icon={<MailOutlined />}
                              loading={
                                sendingInvoiceLoading === item?.invoice?.uuid
                              }
                              onClick={() =>
                                setOpenSendInvoiceConfirm(item?.invoice?.uuid)
                              }
                              disabled={
                                !["Pending", "Pendente"].includes(
                                  item?.invoice?.status || ""
                                ) ||
                                (["Pending", "Pendente"].includes(
                                  item?.invoice?.status || ""
                                ) &&
                                  item?.invoice?.due_date &&
                                  moment(
                                    item?.invoice?.due_date,
                                    "YYYY-MM-DD"
                                  ).isBefore(moment(), "day"))
                              }
                            />
                          </Tooltip>,
                        ]}
                      >
                        <List.Item.Meta
                          title={`Venda ${item?.sale?.id || ""} • Fatura ${
                            item?.invoice?.number || item?.invoice?.id || ""
                          }`}
                          description={`Emissão: ${
                            item?.sale?.date
                              ? moment(item?.sale?.date, "YYYY-MM-DD").format(
                                  "DD/MM/YYYY"
                                )
                              : ""
                          } | ${
                            item?.invoice?.status != "Paid"
                              ? "Vencimento:"
                              : "Fatura paga"
                          } ${
                            item?.invoice?.status != "Paid"
                              ? item?.invoice?.due_date
                                ? moment(
                                    item?.invoice?.due_date,
                                    "YYYY-MM-DD"
                                  ).format("DD/MM/YYYY")
                                : ""
                              : ""
                          } | Valor: ${formatCurrency(
                            parseFloat(item?.invoice?.total_amount || 0)
                          )}`}
                        />
                      </List.Item>
                    )}
                  />
                </Collapse.Panel>
              </Collapse>
            </Col>

            <Col span={24}>
              <Space direction="vertical">
                <Text strong>Discriminação</Text>
                <Text>{details.description}</Text>
              </Space>
            </Col>

            {details.statusHistories.length ? (
              <>
                <Divider />
                <Col span={24} style={{ marginBottom: 5 }}>
                  <List
                    header={<Text strong>Histórico</Text>}
                    itemLayout="horizontal"
                    dataSource={details.statusHistories}
                    renderItem={(item: any, index) => (
                      <List.Item>
                        <List.Item.Meta
                          title={
                            <Badge
                              color={
                                statusColor[
                                  item?.status as keyof typeof statusColor
                                ]
                              }
                              text={
                                contractsStatus.find(
                                  (_status) => _status.value === item?.status
                                )?.label || ""
                              }
                            />
                          }
                          description={`[${moment(
                            item.created_at,
                            "YYYY-MM-DD HH:mm"
                          ).format("DD/MM/YYYY HH:mm")}] ${item.reason}`}
                        />
                      </List.Item>
                    )}
                  />
                </Col>
              </>
            ) : (
              ""
            )}

            {profilePermissionService.hasPermission(
              `contracts-contracts:write`
            ) && (
              <Col span={24} style={{ marginBottom: 15 }}>
                <Button
                  type="primary"
                  block
                  style={{ marginTop: 20 }}
                  onClick={() => {
                    navigate(`/contracts/${details.uuid}/edit`);
                    onCloseDetails();
                  }}
                >
                  Editar
                </Button>
              </Col>
            )}
          </Row>
          <ConfirmModal
            open={!!openSendInvoiceConfirm}
            title="Deseja mesmo enviar a fatura por-email agora?"
            okText="Sim, enviar"
            cancelText="Cancelar"
            onOk={() => {
              const uuid = openSendInvoiceConfirm as string;
              setSendingInvoiceLoading(uuid);
              sendInvoiceMutate({ values: {}, uuid });
              setOpenSendInvoiceConfirm(null);
            }}
            onCancel={() => setOpenSendInvoiceConfirm(null)}
          />
          <ConfirmModal
            open={!!openPayInvoiceConfirm}
            loading={payInvoiceLoading === openPayInvoiceConfirm}
            title="Deseja mesmo quitar esta fatura agora?"
            okText="Sim, quitar"
            cancelText="Cancelar"
            onOk={() => {
              const uuid = openPayInvoiceConfirm as string;
              setPayInvoiceLoading(uuid);
              payInvoiceMutate({
                values: {
                  status: "Paid",
                  paid_at: paymentDate.format("YYYY-MM-DD"),
                },
                uuid,
              });
            }}
            onCancel={() => setOpenPayInvoiceConfirm(null)}
          >
            <div style={{ marginLeft: 31, marginTop: 10 }}>
              <Text strong>Data do Pagamento</Text>
              <DatePicker 
                    style={{ width: '100%', marginTop: 5 }} 
                    format="DD/MM/YYYY"
                    allowClear={false}
                    value={paymentDate}
                    onChange={(date) => setPaymentDate(date || dayjs())}
                />
            </div>
          </ConfirmModal>
        </>
      )}
    </Drawer>
  );
}
